import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Modal,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCalendar,
  IconCheck,
  IconExclamationCircle,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import DateInputComp from "../../commonComponents/components/dateInput/DateInputComp";
import TextInputComp from "../../commonComponents/components/textInput/TextInputComp";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  bulkUpsert,
  editCompletedJourney,
  getAllData,
  getAllDataV2,
  getAllDataV3,
  getAllDataV4,
  getAllDataWithFilters,
  hierarchicalInsert,
} from "../../services/common.service";
import { getAllUsers, getCabinUsers } from "../../services/employees.service";
import Header from "../../commonComponents/layout/Header";
import { useNavigate, useParams } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import {
  compareObject,
  returnOnlyUpdatedItems,
} from "../../utils/objectManuplation.utils";
import InternalOrders from "./journeyEditComponents/InternalOrders";
import CustomerOrders from "./journeyEditComponents/CustomerOrders";
import JourneyLegs from "./journeyEditComponents/JourneyLegs";
import SummaryModal from "./journeyEditComponents/SummaryModal";
import useAuthStore from "../../store/authStore";
import ReactTable from "../../commonComponents/reactTable/ReactTable";
import { createColumnHelper } from "@tanstack/react-table";
import ComboOrders from "./journeyEditComponents/ComboOrders";
import { prepareOrdersPayloadWeb } from "../../utils/prepareOrdersPayloadWeb";

// Email validation regex pattern
const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const JourneyEditForm = () => {
  // Hooks for navigation and getting journey ID from URL
  const navigate = useNavigate();
  const { journeyId } = useParams();
  const { auth } = useAuthStore();
  const userRoleCategory = auth?.emp_category_id?.category_name;
  const hydratedLegRef = useRef(null);

  // State management for active journey leg and its data
  const [activeLeg, setActiveLeg] = useState({ position: 1 });
  const [legData, setLegData] = useState([{}]);
  const [originalLegData, setOriginalLegData] = useState([{}]);

  // State for tracking validation errors in orders
  const [orderErrorObj, setOrderErrorObj] = useState({
    internal: [],
    customer: [],
    combo: [],
  });

  // State for managing customer and internal orders
  const [customerOrders, setCustomerOrders] = useState([]);
  const [internalOrders, setInternalOrders] = useState([]);
  const [comboOrders, setComboOrders] = useState([]);
  const [originalOrders, setOriginalOrders] = useState({
    internal: [],
    customer: [],
    combo: [],
  });

  // UI state management
  const [saveLoading, setSaveLoading] = useState(false);
  const [summaryModal, setSummaryModal] = useState({ modal: false });
  const [endJourneyModal, setEndJourneyModal] = useState({ modal: false });
  const queryClient = useQueryClient();

  // Add state to track original inventory before aircraft change
  const [originalInventory, setOriginalInventory] = useState([]);
  const [retrievalLegs, setRetrievalLegs] = useState([]);

  // Query to fetch all active products with transformed data for select components
  const allProductsQuery = useQuery({
    queryKey: ["all-products"],
    queryFn: () =>
      getAllDataV3({
        source: "products",
        filter: { is_active: true },
        sort: { id: "product_code", desc: false },
        page: -1,
      }),
    select: (data) => {
      return data?.data?.map((item) => ({
        ...item,
        label: item?.product_name,
        value: item?.id?.toString(),
      }));
    },
  });

  // Query to fetch journey legs for the current journey
  const legsQuery = useQuery({
    queryKey: ["all-legs-for-journey", journeyId],
    queryFn: () =>
      getAllDataV3({
        source: "journey_leg",
        page: -1,
        filter: { journey_id: journeyId },
        sort: [{ id: "leg_number", desc: false }],
      }),
  });

  // Query to fetch journey details
  const getJourneyDetails = useQuery({
    queryKey: ["get-journey-details"],
    queryFn: () =>
      getAllDataV4({ source: `items/journey/${journeyId}`, page: -1 }),
    select: (data) => {
      return data?.data?.[0];
    },
  });

  const getComboDetailsQuery = useQuery({
    queryKey: ["get-combo-details"],
    queryFn: () =>
      getAllData({ source: `combo_products`, page: -1 }),
    select: (data) => {
      return data?.data;
    },
  })

  // Query to fetch products for the active leg
  // const legProductsQuery = useQuery({
  //   queryKey: ['leg-products', activeLeg?.id],
  //   queryFn: () => {
  //     // will get the pending retrivales if any for current aircraft
  //     return getAllDataV3({
  //       source: 'journey_leg_inventory',
  //       page: -1,
  //       filter: { journey_leg_id: activeLeg?.id }
  //     }).then(async currentLegInventory => {
  //       if (
  //         activeLeg?.leg_number == 1 &&
  //         !legsQuery?.data?.data?.[0]?.completed_by_cc &&
  //         !legsQuery?.data?.data?.[0]?.completed_by_scc
  //       ) {
  //         return await getAllDataV3({
  //           // eslint-disable-next-line no-unsafe-optional-chaining
  //           page: 1,
  //           source: 'journey_leg',
  //           sort: [
  //             { id: 'journey_date', desc: true },
  //             { id: 'departure_time', desc: true }
  //           ],
  //           filter: {
  //             retrieved: false,
  //             is_last_leg: true,
  //             completed_by_scc: true,
  //             completed_by_cc: true,
  //             aircraft_id: getJourneyDetails?.data?.aircraft_id?.id
  //           },
  //           filtersWithAction: [
  //             {
  //               key: 'journey_id',
  //               value: journeyId,
  //               action: 'neq'
  //             }
  //           ]
  //         }).then(async res => {
  //           if (res?.data?.[0]?.id) {
  //             setRetrievalLegs(res?.data)
  //             return await getAllDataV3({
  //               source: 'journey_leg_inventory',
  //               page: -1,
  //               filter: { journey_leg_id: res?.data?.[0]?.id }
  //             }).then(res => {
  //               return res?.data?.map(item => {
  //                 const existingItem = currentLegInventory?.data?.find(
  //                   i => i?.product_id?.id === item?.product_id?.id
  //                 )
  //                 return {
  //                   ...currentLegInventory?.data?.find(
  //                     i => i?.product_id?.id === item?.product_id?.id
  //                   ),
  //                   opening_cc: existingItem?.opening_cc + item?.closing_cc,
  //                   opening_scc: existingItem?.opening_scc + item?.closing_scc,
  //                   closing_cc: existingItem?.closing_cc + item?.closing_cc,
  //                   closing_scc: existingItem?.closing_scc + item?.closing_scc
  //                 }
  //               })
  //             })
  //           } else {
  //             return currentLegInventory?.data
  //           }
  //         })
  //       } else {
  //         // console.log(
  //         //   legsQuery?.data?.data?.[0]?.completed_by_cc,
  //         //   legsQuery?.data?.data?.[0]?.completed_by_scc
  //         // )

  //         return currentLegInventory?.data
  //       }
  //     })
  //   },
  //   enabled: Boolean(activeLeg?.id && legsQuery?.data?.data?.length)
  //   // select: async data => {
  //   //   const result =
  //   //   console.log(result)

  //   //   return result
  //   // }
  // })

  const legProductsQuery = useQuery({
    queryKey: ["leg-products", activeLeg?.id],
    queryFn: () => {
      // will get the pending retrivales if any for current aircraft
      return getAllDataV3({
        source: "journey_leg_inventory",
        page: -1,
        filter: { journey_leg_id: activeLeg?.id },
      }).then(async (currentLegInventory) => {
        if (
          activeLeg?.leg_number == 1 &&
          !legsQuery?.data?.data?.[0]?.completed_by_cc &&
          !legsQuery?.data?.data?.[0]?.completed_by_scc
        ) {
          return await getAllDataV3({
            // eslint-disable-next-line no-unsafe-optional-chaining
            page: 1,
            source: "journey_leg",
            sort: [
              { id: "journey_date", desc: true },
              { id: "departure_time", desc: true },
            ],
            filter: {
              retrieved: false,
              is_last_leg: true,
              completed_by_scc: true,
              completed_by_cc: true,
              aircraft_id: getJourneyDetails?.data?.aircraft_id?.id,
            },
            filtersWithAction: [
              {
                key: "journey_id",
                value: journeyId,
                action: "neq",
              },
            ],
          }).then(async (res) => {
            if (res?.data?.[0]?.id) {
              setRetrievalLegs(res?.data);
              return await getAllDataV3({
                source: "journey_leg_inventory",
                page: -1,
                filter: { journey_leg_id: res?.data?.[0]?.id },
              }).then((res) => {
                return res?.data?.map((item) => {
                  const existingItem =
                    currentLegInventory?.data?.find(
                      (i) => i?.product_id?.id === item?.product_id?.id
                    ) || {};

                  return {
                    ...existingItem,
                    opening_cc:
                      (existingItem?.opening_cc || 0) + (item?.closing_cc || 0),
                    opening_scc:
                      (existingItem?.opening_scc || 0) +
                      (item?.closing_scc || 0),
                    closing_cc:
                      (existingItem?.closing_cc || 0) + (item?.closing_cc || 0),
                    closing_scc:
                      (existingItem?.closing_scc || 0) +
                      (item?.closing_scc || 0),
                  };
                });
              });
            } else {
              return currentLegInventory?.data;
            }
          });
        }

        // Get the index of the current active leg from legsQuery data
        const prevLegIndex = activeLeg?.position;

        // Retrieve the ID of the previous leg by subtracting 2 from the current leg's position
        // Since arrays are 0-based, this ensures we correctly get the previous leg
        const prevLegId = legsQuery?.data?.data?.[prevLegIndex - 2]?.id;

        // Check if the previous leg exists and if either 'completed_by_cc' or 'completed_by_scc' is false
        if (
          prevLegId &&
          Boolean(
            !legsQuery?.data?.data?.[activeLeg?.position - 1]?.completed_by_cc
          ) ^
            Boolean(
              !legsQuery?.data?.data?.[activeLeg?.position - 1]
                ?.completed_by_scc
            )
        ) {
          return await getAllDataV3({
            source: "journey_leg_inventory",
            page: -1,
            filter: { journey_leg_id: prevLegId },
          }).then((res) => {
            return res?.data?.map((item) => {
              const existingItem =
                currentLegInventory?.data?.find(
                  (i) => i?.product_id?.id === item?.product_id?.id
                ) || {};

              let updatedItem = { ...existingItem };

              // Update SCC fields only if completed_by_scc is false
              if (!legsQuery?.data?.data?.[prevLegIndex]?.completed_by_scc) {
                if ((existingItem?.opening_scc || 0) === 0) {
                  updatedItem.opening_scc = item?.closing_scc || 0; // Update only if existing is 0
                }
                if ((existingItem?.closing_scc || 0) === 0) {
                  updatedItem.closing_scc = item?.closing_scc || 0; // Update only if existing is 0
                }
              }

              // Update CC fields only if completed_by_cc is false
              if (!legsQuery?.data?.data?.[prevLegIndex]?.completed_by_cc) {
                if ((existingItem?.opening_cc || 0) === 0) {
                  updatedItem.opening_cc = item?.closing_cc || 0; // Update only if existing is 0
                }
                if ((existingItem?.closing_cc || 0) === 0) {
                  updatedItem.closing_cc = item?.closing_cc || 0; // Update only if existing is 0
                }
              }

              return updatedItem;
            });
          });
        }

        // Default return current inventory
        return currentLegInventory?.data;
      });
    },
    enabled: Boolean(activeLeg?.id && legsQuery?.data?.data?.length),
    // select: async data => {
    //   const result =
    //   console.log(result)

    //   return result
    // }
  });

  const allPaymentExceptionReasonsQuery = useQuery({
    queryKey: ["all-payment_exception_reasons"],
    queryFn: () =>
      getAllDataWithFilters({
        source: "payment_exception_reasons",
        filter: [{ key: "is_active", value: true }],
        sort: { id: "reason", desc: false },
        page: -1,
      }),
    select: (data) => {
      return data?.data?.map((item) => ({
        ...item,
        label: item?.reason,
        value: item?.reason,
      }));
    },
  });

  const aircraftQuery = useQuery({
    queryKey: ["all-aircrafts"],
    queryFn: () =>
      getAllDataV3({
        source: "aircrafts",
        page: -1,
        filter: { is_active: true },
      }),
    select: (data) => {
      return data?.data?.map((items) => {
        return {
          label: items?.tail_reg_no,
          value: `${items?.id}`,
        };
      });
    },
  });

  const usersQuery = useQuery({
    queryKey: ["all-cabin-users"],
    queryFn: () => getCabinUsers({ page: -1 }),
    select: (data) => {
      return data?.data?.map((items) => {
        return {
          ...items,
          label: `${items?.emp_name} (${items?.emp_code})`,
          value: `${items?.id}`,
        };
      });
    },
  });

  const cockpitUsersQuery = useQuery({
    queryKey: ["all-cockpit-users"],
    queryFn: () =>
      getAllUsers({
        page: -1,
        filter: { "emp_role_id.role_name": "Cockpit Crew" },
      }),
    select: (data) => {
      return data?.data?.map((items) => {
        return {
          ...items,
          label: `${items?.emp_name} (${items?.emp_code})`,
          value: `${items?.id}`,
        };
      });
    },
  });

  const getOrderDetailsQuery = useQuery({
    queryKey: ["get-journey-order-details", activeLeg?.id],
    queryFn: () =>
      getAllDataV4({
        source: `items/journey_order_item?filters={"journey_order_id.journey_leg_id_eq":${activeLeg?.id}}&page=-1&sort_by=id`,
      }),
    enabled: Boolean(activeLeg?.id),
  });

  const allOrdersCountOfJourneyQuery = useQuery({
    queryKey: ["get-journey-order-details-count", journeyId],
    queryFn: () =>
      getAllDataV4({
        source: `items/journey_order?filters={"journey_id_eq":${journeyId}}&fields=journey_leg_id&group_by=["journey_leg_id"]&aggregate={"count":"journey_id"}`,
      }),
    enabled: Boolean(journeyId),
  });

  const getOrderSummaryQuery = useQuery({
    queryKey: ["get-order-summary", activeLeg?.id],
    queryFn: () =>
      getAllDataV2({
        source: `sales_summary`,
        filter: [{ key: "journey_leg_id", value: activeLeg?.id }],
        page: -1,
      }),
    // enabled: Boolean(activeLeg?.id && Boolean(summaryModal?.modal))
    enabled: Boolean(activeLeg?.id),
  });

  const downloadOrdersQuery = useMutation({
    mutationFn: () =>
      getAllDataV2({
        source: `sales_summary`,
        filter: [{ key: "journey_leg_id", value: activeLeg?.id }],
        page: -1,
        download: true,
      }),
    onSuccess: (data) => {
      if (data?.data?.[0]?.url) {
        window.open(data?.data?.[0]?.url, "_blank");
      }
    },
    onError: (e) => {

      notifications.show({
        title: "Error",
        message: e?.message || "Something went wrong",
        autoClose: 3000,
        color: "red",
        icon: <IconX />,
      });
    },
  });

  const saveJourneyMutation = useMutation({
    mutationFn: (body) =>
      hierarchicalInsert({
        source: ["journey_order_item", "journey_order"],
        body,
      }),
    onSuccess: () => {
      notifications.update({
        id: journeyId,
        title: "Order Details Updated",
        message: "Please wait till we update Orders",
        color: "green",
        loading: false,
        autoClose: 3000,
        icon: <IconCheck />,
      });
      queryClient.invalidateQueries([
        "all-journeys",
        "all-legs",
        "leg-products",
        "get-journey-details",
        "get-journey-order-details",
      ]);
    },
    onError: (e) => {
      notifications.update({
        id: journeyId,
        title: "Sorry!",
        message: e || "Something went wrong",
        color: "red",
        loading: false,
        autoClose: 3000,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const saveJourneyLegsMutation = useMutation({
    mutationFn: (data) => bulkUpsert({ source: "journey_leg", body: data }),
    onSuccess: () => {
      notifications.update({
        id: journeyId,
        title: "Journey Details Updated",
        message: "Please wait till we update leg info",
        color: "green",
        loading: false,
        autoClose: 3000,
        icon: <IconCheck />,
      });
    },
    onError: (e) => {
      notifications.update({
        id: journeyId,
        title: "Sorry!",
        message: e || "Something went wrong",
        color: "red",
        loading: false,
        autoClose: 3000,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const saveCompletedJourneyMutation = useMutation({
    mutationFn: (body) =>
      editCompletedJourney({
        id: activeLeg?.id,
        body,
      }),
    onSuccess: () => {
      notifications.update({
        id: journeyId,
        title: "Order Details Updated",
        message: "Please wait till we update Orders",
        color: "green",
        loading: false,
        autoClose: 3000,
        icon: <IconCheck />,
      });
      queryClient.invalidateQueries([
        "all-journeys",
        "all-legs",
        "leg-products",
        "get-journey-details",
        "get-journey-order-details",
      ]);
    },
    onError: (e) => {
      notifications.update({
        id: journeyId,
        title: "Sorry!",
        message: e || "Something went wrong",
        color: "red",
        loading: false,
        autoClose: 3000,
        icon: <IconExclamationCircle />,
      });
    },
  });

  const normalizeComboOrdersForUI = (comboGroups = []) => {
    return comboGroups.map((group, groupIndex) => {
      const order = group.journey_order;

      const items = Array.isArray(order.journey_order_item)
        ? order.journey_order_item
        : [];

      const food = items[0];
      const beverage = items[1];

      const quantities = items.map(i => Number(i.quantity || 0));
      const comboQuantity = quantities.length
        ? Math.min(...quantities)
        : 0;

      const sellPrice = items.reduce(
        (sum, p) => sum + Number(p.unit_price || 0) * Number(p.quantity || 0),
        0
      );

      const comboId =
        order?.combo_id ||
        items?.[0]?.combo_id ||
        null;

      const comboDetails = getComboDetailsQuery.data.find(
        c => Number(c.id) === Number(comboId)
      );

      const derivedSalesType = items.some(
        i => !!i.payment_exception_reasons
      ) ? "FOC" : "Sales";

      const derivedPaymentReason =
        items.find(i => i.payment_exception_reasons)?.payment_exception_reasons
        || order?.payment_exception_reasons
        || null;

      /* --------------------------------------------------
         🔑 CRITICAL PART — PRESERVE ITEM IDS
      -------------------------------------------------- */
      const comboItems = items.map((item, idx) => {
        const comboItem = {
          journey_order_item_id: item.id || null, // 🔑 THIS FIXES DUPLICATES
          product_id: item.product_id,
          unit_price: item.unit_price,
          combo_id: item.combo_id || null,
        };
        return comboItem;
      });

      return {
        quantity: comboQuantity,

        food_ids: food ? [food.product_id] : [],
        beverage_ids: beverage ? [beverage.product_id] : [],

        sell_price: sellPrice,

        journey_order_id: {
          ...order,
          sales_type: derivedSalesType,
          payment_exception_reasons: derivedPaymentReason,

          food_product_name: food?.product_name,
          food_product_code: food?.product_code,
          food_selling_price: food?.unit_price,

          beverage_product_name: beverage?.product_name,
          beverage_product_code: beverage?.product_code,
          beverage_selling_price: beverage?.unit_price,
        },
        combo_details: comboDetails || null,
        _combo_items: comboItems,
      };
    }).filter(Boolean);
  };

  const normalizeCombo = (row) => {
    if (!row) return null;

    const order = row.journey_order_id || {};

    return {
      // top-level
      quantity: Number(row.quantity || 0),
      sell_price: Number(row.sell_price || 0),

      food_ids: [...(row.food_ids || [])].map(Number).sort(),
      beverage_ids: [...(row.beverage_ids || [])].map(Number).sort(),

      // order-level
      journey_order: {
        id: order.id || null,
        seat: order.seat || "",
        sold_by: order.sold_by || "",
        sales_type: order.sales_type || "",
        internal_sales: Boolean(order.internal_sales),

        invoice_email: order.invoice_email || "",
        invoice_mobile: order.invoice_mobile || "",
        invoice_pnr: order.invoice_pnr || "",

        payment_exception_reasons:
          order.payment_exception_reasons || null,
      },

      // items (sorted, normalized)
      items: (order.journey_order_item || [])
        .map((i) => ({
          id: i.id || null,
          product_id: Number(i.product_id),
          quantity: Number(i.quantity || 0),
          unit_price: Number(i.unit_price || 0),
          sale_type: i.sale_type || "Sales",
          payment_exception_reasons: i.payment_exception_reasons || null,
          combo_id: i.combo_id || null,
        }))
        .sort((a, b) => a.product_id - b.product_id),
    };
  };

  useEffect(() => {
    if (!activeLeg?.id) return;

    // 🔥 reset all order states when leg changes
    setCustomerOrders([]);
    setInternalOrders([]);
    setComboOrders([]);

    setOriginalOrders({
      customer: [],
      internal: [],
      combo: [],
    });

    // allow fresh hydration
    hydratedLegRef.current = null;
  }, [activeLeg?.id]);

  // Effect to process and organize order details when data is fetched
  useEffect(() => {
    if (!getOrderDetailsQuery?.data?.data) return;

    const rawData = getOrderDetailsQuery.data.data;

    const normalizeSaleType = (saleType) => {
      if (!saleType) return "individual";
      const t = saleType.toLowerCase();
      if (t === "discount") return "individual";
      return t;
    };

    // const isDiscountSale = (saleType) =>
    //   saleType?.toLowerCase() === "discount";

    const isComboRow = (row) =>
      row?.combo_id != null && row?.combo_sale_id != null;

    /* ==================================================
       1️⃣ CUSTOMER ORDERS (INDIVIDUAL + DISCOUNT)
    ================================================== */
    const customerOrders = rawData
      .filter((row) => {
        if (!row?.quantity || row.quantity === 0) return false;
        if (row?.journey_order_id?.internal_sales) return false;

        // ❌ exclude combo rows
        if (isComboRow(row)) return false;

        const saleType = normalizeSaleType(row.sale_type);
        return saleType === "individual";
      })
      .map((row) => ({
        ...row,
        id: row.id,
        sale_type: "individual",
        // is_discount_applied: isDiscountSale(row.sale_type),
        journey_order_id: {
          ...row.journey_order_id,
          sales_type: row?.payment_exception_reasons ? "FOC" : "Sales",
        },
      }));

    /* ==================================================
       2️⃣ INTERNAL ORDERS
    ================================================== */
    const internalOrders = rawData
      .filter(
        (row) =>
          row?.journey_order_id?.internal_sales &&
          row?.quantity &&
          row.quantity !== 0 &&
          !isComboRow(row)
      )
      .map((row) => {
        const internalUser = usersQuery?.data?.find(
          (u) => u.id === row?.journey_order_id?.internal_user_id
        );

        return {
          ...row,
          id: row.id,
          sale_type: "individual",
          journey_order_id: {
            ...row.journey_order_id,
            sales_type:
              internalUser?.emp_category_id?.category_name === "Cabin"
                ? "Cabin"
                : "Cockpit",
          },
        };
      });

    /* ==================================================
       3️⃣ COMBO ORDERS (GROUP BY combo_sale_id / UUID)
    ================================================== */
    const comboMap = {};

    rawData.forEach((row) => {
      if (!row?.quantity || row.quantity === 0) return;
      if (!isComboRow(row)) return;

      const order = row.journey_order_id;
      const uuid = order?.unique_order_uuid;
      if (!uuid) return;

      if (!comboMap[uuid]) {
        comboMap[uuid] = {
          journey_order: {
            ...(order?.id ? { id: order.id } : {}),
            journey_id: order.journey_id,
            journey_leg_id: order.journey_leg_id,
            purchase_date: order.purchase_date,
            unique_order_uuid: uuid,
            seat: order.seat,
            sold_by: order.sold_by,
            internal_sales: order.internal_sales,
            internal_user_id: order.internal_user_id,
            invoice_pnr: order.invoice_pnr,
            invoice_email: order.invoice_email,
            invoice_mobile: order.invoice_mobile,
            payment_exception_reasons: order.payment_exception_reasons,
            journey_order_item: [],
          },
        };
      }

      comboMap[uuid].journey_order.journey_order_item.push({
        ...(row?.id ? { id: row.id } : {}),
        product_id: row.product_id.id,
        product_name: row.product_name,
        product_code: row.product_code,
        quantity: row.quantity,
        unit_price: row.unit_price,
        mrp: row.mrp ?? 0,
        product_category: row.product_category ?? null,
        combo_id: row.combo_id,
        combo_sale_id: row.combo_sale_id,
        payment_exception_reasons: row.payment_exception_reasons,

        // ✅ UI still needs Sales / FOC
        sale_type: row?.payment_exception_reasons ? "FOC" : "Sales",
      });
    });

    /* ==================================================
       4️⃣ NORMALIZE COMBO FOR UI
    ================================================== */
    const comboOrders = normalizeComboOrdersForUI(
      Object.values(comboMap)
    );

    /* ==================================================
       5️⃣ SET STATE
    ================================================== */
    setCustomerOrders(customerOrders);
    setInternalOrders(internalOrders);
    setComboOrders(comboOrders);

    setOriginalOrders({
      customer: customerOrders,
      internal: internalOrders,
      combo: comboOrders,
    });
  }, [getOrderDetailsQuery?.data?.data, usersQuery?.data]);

  const expandedComboOrders = comboOrders.flatMap(item => {
    const result = [];

    if (item.food_ids?.length) {
      result.push({
        ...item,
        product_id: { id: item.food_ids[0] }
      });
    }

    if (item.beverage_ids?.length) {
      result.push({
        ...item,
        product_id: { id: item.beverage_ids[0] }
      });
    }

    return result;
  });

  // useEffect(() => {
  //   const { payload } = prepareOrdersPayloadWeb({
  //     customerOrders,
  //     internalOrders,
  //     comboOrders,
  //     journeyId,
  //     journeyLegId: activeLeg?.id
  //   });

  //   console.log({ payload });
  // }, [customerOrders, internalOrders, comboOrders]);

  // Function to validate and save journey data
  const checkErrorAndSave = async ({
    isAircraftChange = false,
    lastJourneyInventory,
    newAircraft,
    updatedLegData,
  }) => {

    // Helper function to calculate inventory for previous legs
    const buildCurrentInventoryResult = async ({ prev_leg_id }) => {
      if (prev_leg_id?.length) {
        const allPromise = prev_leg_id?.map((item) =>
          getAllDataV3({
            source: "journey_leg_inventory",
            page: -1,
            filter: { journey_leg_id: item },
          }).then((res) => res?.data)
        );
        const prevLegInventory = await Promise.all([...allPromise]);

        // Calculate finalResult for leg chain
        const finalResult =
          prev_leg_id?.length > 1
            ? prevLegInventory?.map((allLegInv, index) => {
                let legInvArray = [];
                if (index === 0) {
                  legInvArray.push(allLegInv);
                } else {
                  legInvArray.push(
                    allLegInv?.map((item) => {
                      const prevLeg = prevLegInventory?.[index - 1]?.find(
                        (inv) => inv?.product_id?.id == item?.product_id?.id
                      );

                      const closing_cc = item?.opening_cc - item?.closing_cc;
                      const closing_scc = item?.opening_scc - item?.closing_scc;
                      return {
                        ...item,
                        opening_cc: prevLeg?.closing_cc || 0,
                        opening_scc: prevLeg?.closing_scc || 0,
                        closing_cc: prevLeg?.closing_cc - closing_cc || 0,
                        closing_scc: prevLeg?.closing_scc - closing_scc || 0,
                      };
                    })
                  );
                }
                return legInvArray;
              })
            : [prevLegInventory];

        // Only handle aircraft change logic if isAircraftChange is true
        if (isAircraftChange === true && lastJourneyInventory?.length) {
          // Store original inventory for possible revert
          setOriginalInventory(prevLegInventory);

          // Get the last leg inventory and combine with new aircraft inventory
          const lastLegInventory = finalResult[prev_leg_id?.length - 1][0];
          const combinedInventory = lastLegInventory.map((item) => {
            const lastJourneyItem = lastJourneyInventory.find(
              (li) => li.product_id.id === item.product_id.id
            );

            return {
              ...item,
              opening_cc:
                (item.closing_cc || 0) + (lastJourneyItem?.closing_cc || 0),
              opening_scc:
                (item.closing_scc || 0) + (lastJourneyItem?.closing_scc || 0),
            };
          });

          // Calculate final inventory with current sales
          const finalInventory = legProductsQuery?.data?.map((item) => {
            const arrayOfCurrProduct = [
              ...customerOrders,
              ...internalOrders,
              ...expandedComboOrders,
            ].filter((inv) => inv?.product_id?.id == item?.product_id?.id);

            const prevLeg = combinedInventory?.find(
              (i) => i?.product_id?.id === item?.product_id?.id
            );

            const current_cc =
              arrayOfCurrProduct
                ?.filter(
                  (item) =>
                    item?.journey_order_id?.sold_by?.toLowerCase() === "cc"
                )
                .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

            const current_scc =
              arrayOfCurrProduct
                ?.filter(
                  (item) =>
                    item?.journey_order_id?.sold_by?.toLowerCase() === "scc"
                )
                .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

            const prev_opening_cc =
              activeLeg?.position === 1 || isAircraftChange
                ? prevLeg?.closing_cc + item?.opening_cc || 0
                : prevLeg?.closing_cc || item?.opening_cc || 0;
            const prev_opening_scc =
              activeLeg?.position === 1 || isAircraftChange
                ? prevLeg?.closing_scc + item?.opening_scc || 0
                : prevLeg?.closing_scc || item?.opening_scc || 0;

            return {
              ...item,
              opening_cc: prev_opening_cc,
              opening_scc: prev_opening_scc,
              closing_cc:
                current_cc > 0
                  ? (prev_opening_cc || 0) - current_cc
                  : prev_opening_cc,
              closing_scc:
                current_scc > 0
                  ? (prev_opening_scc || 0) - current_scc
                  : prev_opening_scc,
            };
          });
          // return finalInventory
          return await buildFutureInventoryResult({
            currentInventory: finalInventory,
          });
        }

        // If reverting aircraft change, use stored original inventory
        else if (isAircraftChange === false && originalInventory?.length) {
          // return originalInventory
          return await buildFutureInventoryResult({
            currentInventory: originalInventory,
          });
        }

        // Normal inventory calculation (no aircraft change)
        else {
          const finalInventory = legProductsQuery?.data?.map((item) => {
            const arrayOfCurrProduct = [
              ...customerOrders,
              ...internalOrders,
              ...expandedComboOrders,
            ].filter((inv) => inv?.product_id?.id == item?.product_id?.id);

            const prevLeg = finalResult[prev_leg_id?.length - 1][0]?.find(
              (i) => i?.product_id?.id === item?.product_id?.id
            );

            const current_cc =
              arrayOfCurrProduct
                ?.filter(
                  (item) =>
                    item?.journey_order_id?.sold_by?.toLowerCase() === "cc"
                )
                .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

            const current_scc =
              arrayOfCurrProduct
                ?.filter(
                  (item) =>
                    item?.journey_order_id?.sold_by?.toLowerCase() === "scc"
                )
                .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

            const prev_opening_cc =
              (activeLeg?.position === 1 && !activeLeg?.completed_by_cc) ||
              isAircraftChange
                ? prevLeg?.closing_cc + item?.opening_cc || 0
                : prevLeg?.closing_cc === item?.opening_cc
                ? item?.opening_cc
                : prevLeg?.closing_cc -
                    (prevLeg?.closing_cc - item?.opening_cc) || 0;
            const prev_opening_scc =
              (activeLeg?.position === 1 && !activeLeg?.completed_by_scc) ||
              isAircraftChange
                ? prevLeg?.closing_scc + item?.opening_scc || 0
                : prevLeg?.closing_scc === item?.opening_scc
                ? item?.opening_scc
                : prevLeg?.closing_scc -
                    (prevLeg?.closing_scc - item?.opening_scc) || 0;

            return {
              ...item,
              opening_cc: prev_opening_cc,
              opening_scc: prev_opening_scc,
              closing_cc:
                current_cc > 0
                  ? (prev_opening_cc || 0) - current_cc
                  : prev_opening_cc,
              closing_scc:
                current_scc > 0
                  ? (prev_opening_scc || 0) - current_scc
                  : prev_opening_scc,
            };
          });
          // return finalInventory
          return await buildFutureInventoryResult({
            currentInventory: finalInventory,
          });
        }
      }
    };

    /**
     * Asynchronously builds the future inventory result for journey legs beyond the active leg.
     *
     * Fetches inventory data for future journey legs and calculates the inventory
     * based on the current inventory and the fetched data. The function computes
     * the opening and closing inventory for each product in subsequent legs.
     *
     * @param {Object} currentInventory - The current inventory state for the active journey leg.
     * @returns {Promise<Array>} A promise that resolves to an array containing the calculated
     * future inventory for each product in the subsequent journey legs.
     */

    const buildFutureInventoryResult = async ({ currentInventory }) => {
      const allPromise = legData
        ?.filter(
          (i) =>
            i?.leg_number > activeLeg?.leg_number &&
            !["cancelled", "auto cancelled"]?.includes(i?.status?.toLowerCase())
        )
        ?.map((item) =>
          getAllDataV3({
            source: "journey_leg_inventory",
            page: -1,
            filter: { journey_leg_id: item?.id },
          }).then((res) => res?.data)
        );

      const futureLegInventory = await Promise.all([...allPromise]);

      const finalInventoryResult = [];
      if (futureLegInventory?.length) {
        let prevArray = { original: [], modified: [] };
        let currentArray = { original: [], modified: [] };
        futureLegInventory?.map((item, index) => {
          prevArray = {
            original: currentArray.original,
            modified: currentArray.modified,
          };
          currentArray = { original: [], modified: [] };
          item?.map((innerItem) => {
            let calculationObj = {};
            currentArray.original.push(innerItem);
            if (index === 0) {
              calculationObj = {
                ...currentInventory?.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                ),
              };
              prevArray.original = [...legProductsQuery.data];
              prevArray.modified = currentInventory;
            } else {
              calculationObj = {
                ...prevArray.modified?.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                ),
              };
            }
            const prevDelta = {
              cc:
                prevArray.original.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.closing_cc -
                prevArray.modified.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.closing_cc,
              scc:
                prevArray.original.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.closing_scc -
                prevArray.modified.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.closing_scc,
            };

            const result = {
              ...innerItem,
              opening_cc:
                prevArray.modified?.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.journey_leg_id?.aircraft_id ==
                innerItem?.journey_leg_id?.aircraft_id
                  ? calculationObj?.closing_cc
                    ? calculationObj.closing_cc
                    : 0
                  : innerItem?.opening_cc - prevDelta?.cc || 0,
              opening_scc:
                prevArray.modified?.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.journey_leg_id?.aircraft_id ==
                innerItem?.journey_leg_id?.aircraft_id
                  ? calculationObj?.closing_scc
                    ? calculationObj.closing_scc
                    : 0
                  : innerItem?.opening_scc - prevDelta?.scc || 0,
              closing_cc:
                prevArray.modified?.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.journey_leg_id?.aircraft_id !=
                innerItem?.journey_leg_id?.aircraft_id
                  ? innerItem?.closing_cc - prevDelta?.cc
                  : (calculationObj?.closing_cc || 0) ==
                    (innerItem?.closing_cc || 0)
                  ? // calculationObj?.closing_cc - (innerItem?.closing_cc || 0)
                    calculationObj?.closing_cc || 0
                  : (calculationObj?.closing_cc || 0) -
                      (innerItem?.opening_cc - innerItem?.closing_cc || 0) || 0,
              closing_scc:
                prevArray.modified?.find(
                  (i) => i?.product_id?.id === innerItem?.product_id?.id
                )?.journey_leg_id?.aircraft_id !=
                innerItem?.journey_leg_id?.aircraft_id
                  ? innerItem?.closing_scc - prevDelta?.scc
                  : calculationObj?.closing_scc == innerItem?.closing_scc
                  ? // calculationObj?.closing_scc - (innerItem?.closing_scc || 0)
                    calculationObj?.closing_scc
                  : calculationObj?.closing_scc -
                      (innerItem?.opening_scc - innerItem?.closing_scc || 0) ||
                    0,
            };

            finalInventoryResult.push(result);
            currentArray.modified.push(result);
          });
        });
      }

      return [...currentInventory, ...finalInventoryResult];
    };

    // Check for unsaved leg changes
    if (
      !compareObject(
        legData?.[activeLeg?.position - 1],
        originalLegData?.[activeLeg?.position - 1]
      )
    ) {
      notifications.show({
        title: "Warning",
        message:
          "There are some leg changes not saved. Please save it or reset it.",
        color: "red",
        icon: <IconAlertCircle />,
      });
      return;
    }

    // Validate all orders and collect errors
    let isError = false;
    let resultError = { customer: [], internal: [], combo: [] };

    // Validate internal orders (crew orders)
    if (internalOrders?.length) {
      internalOrders.forEach((item) => {
        let error = {};
        if (!item?.journey_order_id?.sold_by) {
          error.sold_by = "Please select sold by";
          isError = true;
        }
        if (!item?.journey_order_id?.internal_user_id) {
          error.internal_user_id = "Please select seat";
          isError = true;
        }
        if (!item?.payment_exception_reasons) {
          error.payment_exception_reasons = "Please select reason";
          isError = true;
        }
        if (!item?.journey_order_id?.sales_type) {
          error.sales_type = "Please select Type";
          isError = true;
        }
        if (!item?.product_id?.id) {
          error.product_id = "Please select product";
          isError = true;
        }
        if (!item?.quantity?.toString()) {
          error.quantity = "Please enter quantity";
          isError = true;
        }
        resultError.internal.push(error);
      });
    }

    // Validate customer orders
    if (customerOrders?.length) {
      customerOrders.forEach((item) => {
        let error = {};
        if (!item?.journey_order_id?.sold_by) {
          error.sold_by = "Please select sold by";
          isError = true;
        }
        if (!item?.journey_order_id?.seat) {
          error.seat = "Please select seat";
          isError = true;
        }
        if (
          item?.journey_order_id?.sales_type === "FOC" &&
          !item?.payment_exception_reasons
        ) {
          error.payment_exception_reasons = "Please select reason";
          isError = true;
        }
        if (!item?.journey_order_id?.sales_type) {
          error.sales_type = "Please select Type";
          isError = true;
        }
        if (!item?.product_id?.id) {
          error.product_id = "Please select product";
          isError = true;
        }
        if (!item?.quantity?.toString()) {
          error.quantity = "Please enter quantity";
          isError = true;
        }
        if (
          item?.journey_order_id?.invoice_email &&
          !regex.test(item?.journey_order_id?.invoice_email)
        ) {
          error.invoice_email = "Please enter valid email address";
          isError = true;
        }
        resultError.customer.push(error);
      });
    }

    if (comboOrders?.length) {
      comboOrders.forEach((item) => {
        console.log({ item });
        let error = {};

        // sold by
        if (!item?.journey_order_id?.sold_by) {
          error.sold_by = "Please select sold by";
          isError = true;
        }

        // seat
        if (!item?.journey_order_id?.seat) {
          error.seat = "Please select seat";
          isError = true;
        }

        // sales type (Sales / FOC)
        if (!item?.journey_order_id?.sales_type) {
          error.sales_type = "Please select Type";
          isError = true;
        }

        // FOC reason
        if (
          item?.journey_order_id?.sales_type === "FOC" &&
          !item?.journey_order_id?.payment_exception_reasons
        ) {
          error.payment_exception_reasons = "Please select reason";
          isError = true;
        }

        // food
        if (!item?.food_ids || item.food_ids.length === 0) {
          error.food_ids = "Please select food";
          isError = true;
        }

        // beverage
        if (!item?.beverage_ids || item.beverage_ids.length === 0) {
          error.beverage_ids = "Please select beverage";
          isError = true;
        }

        // quantity
        if (!item?.quantity?.toString()) {
          error.quantity = "Please enter quantity";
          isError = true;
        }

        // invoice email (optional validation)
        if (
          item?.journey_order_id?.invoice_email &&
          !regex.test(item?.journey_order_id?.invoice_email)
        ) {
          error.invoice_email = "Please enter valid email address";
          isError = true;
        }

        resultError.combo.push(error);
      });
    }

    setOrderErrorObj({ ...resultError });

    // If there are errors, show a notification and prevent saving
    if (isError) {
      setSaveLoading(false);
      notifications.show({
        title: "Error",
        message: "Please fill all the mandatory fields",
        color: "red",
        icon: <IconX />,
      });
      return;
    }

    // If there are no errors, show a notification and start saving
    if (!isError) {
      notifications.show({
        id: journeyId,
        title: "Updating Orders...",
        message: "Please do not close anything.",
        loading: true,
        autoClose: false,
      });
      let legInventory = [];

      const { payload } = prepareOrdersPayloadWeb({
        customerOrders,
        internalOrders,
        comboOrders,
        journeyId,
        journeyLegId: activeLeg?.id
      });

      let body = payload.map(order => ({
        journey_order: order.journey_order ?? order
      }));


      if (retrievalLegs?.length) {
        body = [
          ...body,
          ...retrievalLegs.map((item) => ({
            journey_leg: {
              ...item,
              journey_id: item?.journey_id?.id,
              depart_airport_id: item?.depart_airport_id?.id,
              arrive_airport_id: item?.arrive_airport_id?.id,
              senior_cabin_crew_id: item?.senior_cabin_crew_id?.id,
              cabin_crew_id: item?.cabin_crew_id?.id,
              created_by: item?.created_by?.id,
              updated_by: item?.updated_by?.id,
              aircraft_id: item?.aircraft_id?.id,
              retrieved: true,
            },
          })),
        ];
      }

      if (getJourneyDetails?.data?.journey_status !== "completed") {
        let prev_leg_id;
        if (isAircraftChange) {
          prev_leg_id = await getAllDataV3({
            // eslint-disable-next-line no-unsafe-optional-chaining
            page: 1,
            source: "journey_leg",
            sort: [
              { id: "journey_date", desc: true },
              { id: "departure_time", desc: true },
            ],
            filter: {
              retrieved: false,
              is_last_leg: true,
              completed_by_scc: true,
              completed_by_cc: true,
              aircraft_id: isAircraftChange
                ? newAircraft
                : getJourneyDetails?.data?.aircraft_id?.id,
            },
            filtersWithAction: [
              {
                key: "journey_id",
                value: journeyId,
                action: "neq",
              },
            ],
          }).then((res) => {
            if (res?.data?.[0]?.id) {
              // setRetrievalLegs([...retrievalLegs, res?.data?.[0]])
              return [res?.data?.[0]?.id];
            }
          });
        } else {
          prev_leg_id = legData
            ?.filter((item) => Number(item?.leg_number) < activeLeg?.position)
            ?.map((item) => item?.id);
        }
        if (prev_leg_id?.length) {
          legInventory = await buildCurrentInventoryResult({ prev_leg_id });
        } else {
          const currentInventory = legProductsQuery?.data?.map((item) => {
            const arrayOfCurrProduct = [
              ...customerOrders,
              ...internalOrders,
              ...expandedComboOrders,
            ].filter((inv) => inv?.product_id?.id == item?.product_id?.id);
            const current_cc =
              arrayOfCurrProduct
                ?.filter(
                  (item) =>
                    item?.journey_order_id?.sold_by?.toLowerCase() === "cc"
                )
                .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

            const current_scc =
              arrayOfCurrProduct
                ?.filter(
                  (item) =>
                    item?.journey_order_id?.sold_by?.toLowerCase() === "scc"
                )
                .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;
            return {
              ...item,
              closing_cc: (item?.opening_cc || 0) - current_cc,
              closing_scc: (item?.opening_scc || 0) - current_scc,
            };
          });
          legInventory = await buildFutureInventoryResult({ currentInventory });
        }

        const currentLegData = updatedLegData?.length
          ? updatedLegData?.find((item) => item?.id == activeLeg?.id)
          : legData?.find((item) => item?.id == activeLeg?.id);

        const safeLegInventory = legInventory.filter(
          (item) => item?.journey_leg_id?.id && item?.product_id?.id
        );

        body = [
          ...body,
          {
            journey_leg: {
              ...currentLegData,
              completed_by_cc: true,
              completed_by_scc: true,
            },
          },
          // ...retrievalLegs.map(item => ({
          //   journey_leg: {
          //     ...item,
          //     retrieved: true
          //   }
          // })),
          ...safeLegInventory.map((item) => ({
            journey_leg_inventory: {
              ...item,
              journey_leg_id: item?.journey_leg_id?.id,
              product_id: item?.product_id?.id,
              created_by: item?.created_by?.id,
              updated_by: item?.updated_by?.id,
            },
          })),
        ];

        saveJourneyMutation.mutate(body, {
          onSettled: () => {
            setSaveLoading(false);
            setEndJourneyModal({});
          },
        });
      } else {
        if (
          legsQuery?.data?.data?.length &&
          !legsQuery?.data?.data?.find((i) => i?.is_last_leg)?.retrieved
        ) {
          const prev_leg_id = legData
            ?.filter((item) => Number(item?.leg_number) < activeLeg?.position)
            ?.map((item) => item?.id);
          if (prev_leg_id?.length) {
            legInventory = await buildCurrentInventoryResult({ prev_leg_id });
          } else {
            const currentInventory = legProductsQuery?.data?.map((item) => {
              const arrayOfCurrProduct = [
                ...customerOrders,
                ...internalOrders,
                ...expandedComboOrders,
              ].filter((inv) => inv?.product_id?.id == item?.product_id?.id);
              const current_cc =
                arrayOfCurrProduct
                  ?.filter(
                    (item) =>
                      item?.journey_order_id?.sold_by?.toLowerCase() === "cc"
                  )
                  .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;

              const current_scc =
                arrayOfCurrProduct
                  ?.filter(
                    (item) =>
                      item?.journey_order_id?.sold_by?.toLowerCase() === "scc"
                  )
                  .reduce((acc, curr) => acc + curr?.quantity, 0) || 0;
              return {
                ...item,
                closing_cc: (item?.opening_cc || 0) - current_cc,
                closing_scc: (item?.opening_scc || 0) - current_scc,
              };
            });
            legInventory = await buildFutureInventoryResult({
              currentInventory,
            });
          }

          const currentLegData = updatedLegData?.length
            ? updatedLegData?.find((item) => item?.id == activeLeg?.id)
            : legData?.find((item) => item?.id == activeLeg?.id);

          const safeLegInventory = legInventory.filter(
            (item) => item?.journey_leg_id?.id && item?.product_id?.id
          );
          body = [
            ...body,
            {
              journey_leg: {
                ...currentLegData,
                completed_by_cc: true,
                completed_by_scc: true,
              },
            },
            ...safeLegInventory.map((item) => ({
              journey_leg_inventory: {
                ...item,
                journey_leg_id: item?.journey_leg_id?.id,
                product_id: item?.product_id?.id,
                created_by: item?.created_by?.id,
                updated_by: item?.updated_by?.id,
              },
            })),
          ];

          saveJourneyMutation.mutate(body, {
            onSettled: () => {
              setSaveLoading(false);
              setEndJourneyModal({});
            },
          });
        } else {
          // 1️⃣ customer & internal (simple diff)
          const updatedCustomer = returnOnlyUpdatedItems(
            originalOrders.customer,
            customerOrders
          );

          const updatedInternal = returnOnlyUpdatedItems(
            originalOrders.internal,
            internalOrders
          );

          // 2️⃣ combo diff using normalizeCombo
          const isComboModified = (original, current) => {
            if (!original || !current) return true;

            const o = normalizeCombo(original);
            const c = normalizeCombo(current);

            return JSON.stringify(o) !== JSON.stringify(c);
          };

          const updatedCombo = comboOrders.filter((currentRow) => {
            const originalRow = originalOrders.combo.find(
              (o) =>
                o.journey_order_id?.id === currentRow.journey_order_id?.id
            );

            return isComboModified(originalRow, currentRow);
          });

          // 3️⃣ merge all updated rows
          const updatedItems = [
            ...updatedCustomer,
            ...updatedInternal,
            ...updatedCombo,
          ];

          if (!updatedItems.length) {
            notifications.update({
              id: journeyId,
              title: "No Updates",
              message: "Please edit or add items to save",
              loading: false,
              autoClose: 3000,
            });
            setEndJourneyModal({ modal: false });
            setSaveLoading(false);
            return;
          }

          // 4️⃣ classify correctly
          const updatedCustomerOrders = updatedItems.filter(
            (i) =>
              !i.journey_order_id?.internal_sales &&
              !i.food_ids?.length &&
              !i.beverage_ids?.length &&
              !i._combo_items?.length
          );

          const updatedInternalOrders = updatedItems.filter(
            (i) => i.journey_order_id?.internal_sales
          );

          const updatedComboOrders = updatedItems.filter(
            (i) =>
              i.food_ids?.length ||
              i.beverage_ids?.length ||
              i._combo_items?.length
          );

          // 5️⃣ generate payload
          const { payload } = prepareOrdersPayloadWeb({
            customerOrders: updatedCustomerOrders,
            internalOrders: updatedInternalOrders,
            comboOrders: updatedComboOrders,
            journeyId,
            journeyLegId: activeLeg?.id,
          });

          console.log("payload", payload);

          const completedBody = payload.map((order) => ({
            journey_order: order.journey_order,
          }));

          console.log("completedBody", completedBody);

          saveCompletedJourneyMutation.mutate(completedBody, {
            onSettled: () => {
              setSaveLoading(false);
              setEndJourneyModal({});
            },
          });
        }
      }
    }
  };

  // Handler for aircraft changes
  const handleAircraftChange = ({
    oldAircraft,
    newAircraft,
    position,
    lastJourneyInventory,
    updatedLegData,
  }) => {
    // If reverting to original aircraft
    const isRevert = originalLegData[position - 1]?.aircraft_id === newAircraft;
    setLegData(updatedLegData);

    // will get all inventory for all legs
    // will check if any retrivales is there for selected aircraft
    // if any then will change all the inventory else will use the current inventory
    //

    saveJourneyLegsMutation.mutate(updatedLegData, {
      onSettled: () => {
        // Recalculate inventory
        checkErrorAndSave({
          isAircraftChange: !isRevert,
          lastJourneyInventory: lastJourneyInventory?.data,
          newAircraft,
          updatedLegData,
        });
      },
    });
    // checkErrorAndSave({
    //   isAircraftChange: !isRevert,
    //   lastJourneyInventory: lastJourneyInventory?.data,
    //   newAircraft
    // })
    // await queryClient.invalidateQueries({ queryKey: ['all-legs-for-journey', journeyId] })
  };

  const canEditLeg = (() => {
    const role = userRoleCategory?.toLowerCase();
    const { completed_by_cc, completed_by_scc } = activeLeg;

    // Super Admin can always edit
    if (role?.toLowerCase() === "super admin") return true;

    // If the journey is completed, only super admin can edit
    if (getJourneyDetails?.data?.journey_status?.toLowerCase() === "completed")
      return false;

    // Admin can edit only if at least one of completed_by_cc or completed_by_scc is false
    if (role.includes("admin") && role !== "super admin") {
      return !completed_by_cc || !completed_by_scc; // At least one is false
    }

    // Default: No edit permission
    return false;
  })();

  const columnHelper = createColumnHelper();
  const column = [
    columnHelper.accessor("journey_date", {
      header: "Journey Date",
    }),
    columnHelper.accessor("route", {
      header: "Route",
    }),
    columnHelper.accessor("scc_or_cc", {
      header: "SCC/CC",
    }),
    columnHelper.accessor("reason_for_foc", {
      header: "Reason for FOC",
      cell: (value) => value.row.original.reason_for_foc || "-",
    }),
    columnHelper.accessor("consumed_by", {
      header: "Consumed by",
    }),
    columnHelper.accessor("product", {
      header: "Product",
    }),
    columnHelper.accessor("qty", {
      header: "Quantity",
    }),
    columnHelper.accessor("price", {
      header: "Selling Price",
      cell: ({ row }) =>
        row.original?.price ? `₹ ${row.original?.price}` : "₹ 0",
    }),
    columnHelper.accessor("foc_value", {
      header: "FOC Value",
      cell: ({ row }) =>
        row.original?.foc_value ? `₹ ${row.original?.foc_value}` : "₹ 0",
    }),
    columnHelper.accessor("sales_value", {
      header: "Sales Value",
      cell: ({ row }) =>
        row.original?.sales_value ? `₹ ${row.original?.sales_value}` : "₹ 0",
    }),
  ];

  const getSaleType = (o) => o.sale_type?.trim().toLowerCase();

  const isCustomerSale = (o) =>
    !o.internal_sales && getSaleType(o) === "normal";

  const isComboSale = (o) =>
    !o.internal_sales && getSaleType(o) && getSaleType(o) !== "normal";

  return (
    <Box>
      <Group gap={0}>
        <ActionIcon variant="transparent" onClick={() => navigate("/journeys")}>
          <IconArrowLeft size={20} color="gray" />
        </ActionIcon>
        <Header title={"Sales Entry"} />
      </Group>

      <Flex direction={"column"} gap={4} mt={"md"}>
        <Box flex={1}>
          <Grid>
            <Grid.Col span={3}>
              <DateInputComp
                isRequired
                label={"Journey Date"}
                value={
                  getJourneyDetails?.data?.journey_date
                    ? new Date(getJourneyDetails?.data?.journey_date)
                    : null
                }
                rightSection={<IconCalendar size={16} />}
                placeholder={"Select Journey Date"}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={2}>
              <TextInputComp
                isRequired
                label={"Sector"}
                placeholder={"Sector"}
                value={getJourneyDetails?.data?.sector_pattern}
                disabled
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <JourneyLegs
                setActiveLeg={setActiveLeg}
                journeyDate={getJourneyDetails?.data?.journey_date}
                activeLeg={activeLeg}
                legData={legData}
                setLegData={setLegData}
                legsQuery={legsQuery}
                usersQuery={usersQuery}
                setOriginalLegData={setOriginalLegData}
                originalLegData={originalLegData}
                aircraftQuery={aircraftQuery}
                journeysOrderCount={allOrdersCountOfJourneyQuery?.data?.data}
                isAddNew={
                  getJourneyDetails?.data?.journey_status !== "completed"
                }
                onAircraftChange={handleAircraftChange}
                canDelete={canEditLeg}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Tabs defaultValue={"customer_sales"}>
                <Tabs.List style={{ position: "relative" }}>
                  <Tabs.Tab value="customer_sales">Customer Sales</Tabs.Tab>
                  <Tabs.Tab value="internal">{`Internal (cabin/cockpit)`}</Tabs.Tab>
                  <Tabs.Tab value="combo_sales">{`Combo Sales`}</Tabs.Tab>
                  <Box style={{ position: "absolute", right: 0 }}>
                    <Button
                      variant="transparent"
                      size="xs"
                      onClick={() => setSummaryModal({ modal: true })}
                    >
                      View Summary
                    </Button>
                  </Box>
                </Tabs.List>
                <Tabs.Panel value="customer_sales">
                  {canEditLeg ? (
                    <CustomerOrders
                      allProducts={allProductsQuery}
                      allPaymentReason={allPaymentExceptionReasonsQuery}
                      activeLeg={activeLeg}
                      usersQuery={usersQuery}
                      legProductsQuery={legProductsQuery}
                      // checkErrorAndSave={checkErrorAndSave}
                      isReadOnly={
                        !compareObject(
                          legData?.[activeLeg?.position - 1],
                          originalLegData?.[activeLeg?.position - 1]
                        )
                      }
                      addNewCustomerSales={() =>
                        setCustomerOrders([
                          ...customerOrders,
                          {
                            is_new: true,
                            journey_id: journeyId,
                            journey_leg_id: activeLeg?.id,
                            journey_order_id: {
                              internal_sales: false,
                              purchase_date: activeLeg?.journey_date,
                              sale_type: "normal",
                            },
                          },
                        ])
                      }
                      customerSalesData={[...customerOrders]}
                      // setCustomerSalesData={setCustomerOrders}
                      setCustomerSalesData={setCustomerOrders}
                      errorsObj={orderErrorObj.customer}
                      getCustomerSalesDetailsQuery={getOrderDetailsQuery}
                    />
                  ) : (
                    <ReactTable
                      rowData={
                        getOrderSummaryQuery?.data?.data?.filter(
                          isCustomerSale
                        ) || []
                      }
                      columnData={column}
                      useApiPagination={false}
                      loading={getOrderSummaryQuery?.isLoading}
                    />
                  )}
                </Tabs.Panel>
                <Tabs.Panel value="internal">
                  {canEditLeg ? (
                    <InternalOrders
                      allProducts={allProductsQuery}
                      allPaymentReason={allPaymentExceptionReasonsQuery}
                      activeLeg={activeLeg}
                      usersQuery={usersQuery}
                      cockpitUsersQuery={cockpitUsersQuery}
                      legProductsQuery={legProductsQuery}
                      // checkErrorAndSave={checkErrorAndSave}
                      internalSalesData={internalOrders}
                      setInternalSalesData={setInternalOrders}
                      errorsObj={orderErrorObj.internal}
                      getInternalSalesDetails={getOrderDetailsQuery}
                      addNewInternalSales={() =>
                        setInternalOrders([
                          ...internalOrders,
                          {
                            journey_id: journeyId,
                            journey_leg_id: activeLeg?.id,
                            journey_order_id: {
                              internal_sales: true,
                              seat: "",
                              purchase_date: activeLeg?.journey_date,
                            },
                          },
                        ])
                      }
                      isReadOnly={
                        !compareObject(
                          legData?.[activeLeg?.position - 1],
                          originalLegData?.[activeLeg?.position - 1]
                        )
                      }
                    />
                  ) : (
                    <ReactTable
                      rowData={
                        getOrderSummaryQuery?.data?.data?.filter(
                          (order) => order.internal_sales == true
                        ) || []
                      }
                      columnData={column}
                      useApiPagination={false}
                      loading={getOrderSummaryQuery.isLoading}
                    />
                  )}
                </Tabs.Panel>
                <Tabs.Panel value="combo_sales">
                  {canEditLeg ? (
                    <ComboOrders
                      allProducts={allProductsQuery}
                      getComboDetailsQuery={getComboDetailsQuery?.data}
                      allPaymentReason={allPaymentExceptionReasonsQuery}
                      activeLeg={activeLeg}
                      usersQuery={usersQuery}
                      legProductsQuery={legProductsQuery}
                      // checkErrorAndSave={checkErrorAndSave}
                      isReadOnly={
                        !compareObject(
                          legData?.[activeLeg?.position - 1],
                          originalLegData?.[activeLeg?.position - 1]
                        )
                      }
                      addNewCustomerSales={() =>
                        setComboOrders([
                          ...comboOrders,
                          {
                            is_new: true,
                            journey_id: journeyId,
                            journey_leg_id: activeLeg?.id,
                            journey_order_id: {
                              internal_sales: false,
                              purchase_date: activeLeg?.journey_date,
                              sales_type: "Sales",          // ✅ DEFAULT
                              payment_exception_reasons: null
                            },
                            food_ids: [],
                            beverage_ids: [],
                            quantity: "",
                          },
                        ])
                      }
                      customerSalesData={[...comboOrders]}
                      // customerSalesData={comboOrders.filter(isComboSale)}
                      setCustomerSalesData={setComboOrders}
                      errorsObj={orderErrorObj.combo}
                      getCustomerSalesDetailsQuery={getOrderDetailsQuery}
                    />
                  ) : (
                    <ReactTable
                      rowData={
                        getOrderSummaryQuery?.data?.data?.filter(isComboSale) ||
                        []
                      }
                      columnData={column}
                      useApiPagination={false}
                      loading={getOrderSummaryQuery?.isLoading}
                    />
                  )}
                </Tabs.Panel>
              </Tabs>

              {(userRoleCategory?.toLowerCase() === "super admin" ||
                getJourneyDetails?.data?.journey_status.toLowerCase() !==
                  "completed") &&
                !["cancelled", "auto canceled"]?.includes(
                  activeLeg?.status
                ) && (
                  <Group justify="flex-end" mt={"md"}>
                    <Button
                      size="xs"
                      variant="outline"
                      color="gray"
                      onClick={() => {
                        setCustomerOrders([
                          ...getOrderDetailsQuery.data.data
                            .filter(
                              (item) => !item?.journey_order_id?.internal_sales
                            )
                            .map((item) => ({
                              ...item,
                              journey_order_id: {
                                ...item?.journey_order_id,
                                sales_type: item?.payment_exception_reasons
                                  ? "FOC"
                                  : "Sales",
                              },
                            })),
                        ]);
                        setInternalOrders([
                          // eslint-disable-next-line no-unsafe-optional-chaining
                          ...getOrderDetailsQuery.data.data
                            .filter(
                              (item) => item?.journey_order_id?.internal_sales
                            )
                            ?.map((item) => ({
                              ...item,
                              journey_order_id: {
                                ...item?.journey_order_id,
                                sales_type: item?.journey_order_id
                                  ?.internal_user_id
                                  ? usersQuery?.data?.find(
                                      (user) =>
                                        item?.journey_order_id
                                          ?.internal_user_id == user.id
                                    )?.emp_category_id?.category_name == "Cabin"
                                    ? "Cabin"
                                    : "Cockpit"
                                  : null,
                              },
                            })),
                        ]);
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      size="xs"
                      color="teal"
                      onClick={async () => {
                        if (
                          activeLeg?.is_last_leg &&
                          activeLeg?.journey_status !== "Completed"
                        ) {
                          setEndJourneyModal({ modal: true });
                        } else {
                          setSaveLoading(true);
                          checkErrorAndSave({
                            isAircraftChange: false,
                            lastJourneyInventory: null,
                          });
                        }
                      }}
                      // loading={saveLoading}
                    >
                      {activeLeg?.is_last_leg &&
                      activeLeg?.journey_status !== "Completed"
                        ? "End Journey"
                        : "Save"}
                    </Button>
                  </Group>
                )}
              {/* <Group justify='flex-end' mt={'md'}>
                <Button
                  size='xs'
                  variant='outline'
                  color='gray'
                  onClick={() => {
                    setCustomerOrders([
                      ...getOrderDetailsQuery.data.data
                        .filter(item => !item?.journey_order_id?.internal_sales)
                        .map(item => ({
                          ...item,
                          journey_order_id: {
                            ...item?.journey_order_id,
                            sales_type: item?.payment_exception_reasons
                              ? 'FOC'
                              : 'Sales'
                          }
                        }))
                    ])
                    setInternalOrders([
                      // eslint-disable-next-line no-unsafe-optional-chaining
                      ...getOrderDetailsQuery.data.data
                        .filter(item => item?.journey_order_id?.internal_sales)
                        ?.map(item => ({
                          ...item,
                          journey_order_id: {
                            ...item?.journey_order_id,
                            sales_type: item?.journey_order_id?.internal_user_id
                              ? usersQuery?.data?.find(
                                user =>
                                  item?.journey_order_id?.internal_user_id ==
                                  user.id
                              )?.emp_category_id?.category_name == 'Cabin'
                                ? 'Cabin'
                                : 'Cockpit'
                              : null
                          }
                        }))
                    ])
                  }}
                >
                  Reset
                </Button>
                <Button
                  size='xs'
                  color='teal'
                  onClick={async () => {
                    if (
                      activeLeg?.is_last_leg &&
                      activeLeg?.journey_status !== 'Completed'
                    ) {
                      setEndJourneyModal({ modal: true })
                    } else {
                      setSaveLoading(true)
                      checkErrorAndSave({
                        isAircraftChange: false,
                        lastJourneyInventory: null
                      })
                    }
                  }}
                // loading={saveLoading}
                >
                  {activeLeg?.is_last_leg &&
                    activeLeg?.journey_status !== 'Completed'
                    ? 'End Journey'
                    : 'Save'}
                </Button>
              </Group> */}
            </Grid.Col>
          </Grid>
        </Box>
      </Flex>
      <Modal
        opened={endJourneyModal?.modal}
        onClose={() => setEndJourneyModal({ modal: false })}
        title={<Title size={"md"}>End Journey?</Title>}
      >
        <Text>Once confirmed the journey will be marked as completed.</Text>
        <Group justify="flex-end" mt={"md"}>
          <Button
            color={"gray"}
            variant="light"
            size="xs"
            onClick={() => setEndJourneyModal({ modal: false })}
          >
            Cancel
          </Button>
          <Button
            color={"teal"}
            size="xs"
            loading={saveLoading}
            onClick={() => {
              setSaveLoading(true);
              checkErrorAndSave({
                isAircraftChange: false,
                lastJourneyInventory: null,
              });
            }}
          >
            Confirm
          </Button>
        </Group>
      </Modal>
      <SummaryModal
        opened={summaryModal?.modal}
        onDownload={downloadOrdersQuery}
        downloadLoading={downloadOrdersQuery?.isPending}
        onClose={setSummaryModal}
        data={getOrderSummaryQuery}
      />
    </Box>
  );
};

export default JourneyEditForm;
